import { useState } from 'react';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import MoreVertOutlinedIcon from '@mui/icons-material/MoreVertOutlined';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Link,
  Menu,
  MenuItem,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import { APPLIES_TO_OPTIONS, GATE_KIND_META } from '../constants';
import type { Checkpoint, GateKind, PatientApplicability } from '../types';

/**
 * Booking requirements as a single "required to book" list (GDN-2450). The
 * earlier three-checkpoint funnel collapsed to one section: every placed
 * check must be satisfied before a time is reserved. Gates persisted at other
 * checkpoints still show here; new checks are always placed at booking.
 *
 * Presentational only — BookingGatesFunnelSection supplies the data and
 * handlers — so it stays freely storyable.
 */

/** Compact labels for the per-gate applicability segmented control. */
const APPLIES_TO_SHORT: Record<PatientApplicability, string> = {
  new: 'New',
  returning: 'Returning',
  all: 'Everyone',
};

/**
 * Gate kinds a practice can place. Two kinds are intentionally non-placeable:
 * - `fit` is a fast-follow per the PRD — surfaced as a "Coming soon" teaser.
 * - `eligibility` is driven by the insurance-verification switch, not a row.
 */
const PLACEABLE_GATE_KINDS: GateKind[] = ['card', 'paperwork'];

interface ComingSoonCheck {
  label: string;
  description: string;
}

/**
 * Planned-but-not-yet-functional checks, shown disabled so practices can see
 * what's coming. `fit` is a fast-follow in the PRD: a short questionnaire
 * that filters by practice-defined rules.
 */
const COMING_SOON: ComingSoonCheck[] = [
  {
    label: 'Good-fit screening',
    description:
      'A 2–3 question screen so only patients who match your practice — by severity, age, or service — reach your calendar.',
  },
];

export interface FunnelGate {
  kind: GateKind;
  checkpoint: Checkpoint;
  appliesTo: PatientApplicability;
  /** Paperwork gates: the intake form patients complete in-funnel (GDN-2436). */
  formDefinitionId?: string | null;
}

export interface IntakeFormOption {
  form_id: string;
  title: string;
}

export interface BookingGatesFunnelViewProps {
  gates: FunnelGate[];
  showCoverage: boolean;
  subjectPlural: string;
  isLoading?: boolean;
  hasError?: boolean;
  /** Whether the practice can actually collect payments (Stripe Connect
   * charges + payouts enabled). When false, a placed card gate is inert, so
   * its row shows a "set up payments" warning. Defaults to true so the pure
   * view stays storyable without wiring the payments status. */
  stripePaymentsReady?: boolean;
  onAddGate?: (kind: GateKind) => void;
  onRemoveGate?: (kind: GateKind) => void;
  onChangeAppliesTo?: (kind: GateKind, value: PatientApplicability) => void;
  /** The org's assignable forms, for the forms gate's picker. */
  intakeFormOptions?: IntakeFormOption[];
  onChangeIntakeForm?: (kind: GateKind, formDefinitionId: string | null) => void;
  onToggleCoverage?: (enabled: boolean) => void;
  /** Navigate to the payments setup surface (billing settings → patient
   * payments). Invoked from the card gate's inactive-warning link. */
  onSetupPayments?: () => void;
}

export function BookingGatesFunnelView({
  gates,
  showCoverage,
  subjectPlural,
  intakeFormOptions = [],
  onChangeIntakeForm,
  isLoading = false,
  hasError = false,
  stripePaymentsReady = true,
  onAddGate,
  onRemoveGate,
  onChangeAppliesTo,
  onToggleCoverage,
  onSetupPayments,
}: BookingGatesFunnelViewProps) {
  const placedKinds = new Set(gates.map(gate => gate.kind));
  const addable = PLACEABLE_GATE_KINDS.filter(kind => !placedKinds.has(kind));

  return (
    <Card elevation={1} sx={{ borderRadius: 2, border: 1, borderColor: 'grey.200' }}>
      <CardHeader
        avatar={<ShieldOutlinedIcon color="primary" />}
        title="Required to book"
        slotProps={{ title: { variant: 'h6', fontWeight: 600 } }}
        subheader={`Checks ${subjectPlural} must complete before a time is reserved.`}
      />
      <CardContent sx={{ pt: 0 }}>
        {hasError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Couldn’t load booking requirements. Try refreshing.
          </Alert>
        )}
        {isLoading ? (
          <Stack sx={{ alignItems: 'center', py: 4 }}>
            <CircularProgress aria-label="Loading booking requirements" />
          </Stack>
        ) : (
          <Stack spacing={1}>
            {gates.length === 0 && (
              <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>
                No requirements yet — anyone can book.
              </Typography>
            )}
            {gates.map(gate => (
              <PlacedGateRow
                key={gate.kind}
                gate={gate}
                subjectPlural={subjectPlural}
                stripePaymentsReady={stripePaymentsReady}
                intakeFormOptions={intakeFormOptions}
                onRemove={onRemoveGate}
                onChangeAppliesTo={onChangeAppliesTo}
                onChangeIntakeForm={onChangeIntakeForm}
                onSetupPayments={onSetupPayments}
              />
            ))}
            {COMING_SOON.map(check => (
              <ComingSoonRow
                key={check.label}
                label={check.label}
                description={check.description.replace(/patients/g, subjectPlural)}
              />
            ))}
            <AddCheckPicker addable={addable} onAdd={kind => onAddGate?.(kind)} />

            <Divider sx={{ pt: 1 }}>
              <Typography variant="overline" color="text.secondary">
                During booking
              </Typography>
            </Divider>

            <Box sx={{ border: 1, borderColor: 'grey.200', borderRadius: 2, p: 2 }}>
              <Stack
                direction="row"
                spacing={1.5}
                sx={{
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                }}
              >
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Verify insurance at booking
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {subjectPlural[0].toUpperCase() + subjectPlural.slice(1)} add their plan and a
                    photo of their card, and we run a live coverage check — so you get verified
                    benefits on file and they see their estimated copay before picking a time. Never
                    turns anyone away.
                  </Typography>
                </Box>
                <Switch
                  checked={showCoverage}
                  onChange={(_event, checked) => onToggleCoverage?.(checked)}
                  slotProps={{
                    input: {
                      'aria-label': 'Verify insurance at booking',
                    },
                  }}
                />
              </Stack>
            </Box>
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}

interface PlacedGateRowProps {
  subjectPlural: string;
  gate: FunnelGate;
  stripePaymentsReady: boolean;
  intakeFormOptions: IntakeFormOption[];
  onRemove?: (kind: GateKind) => void;
  onChangeAppliesTo?: (kind: GateKind, value: PatientApplicability) => void;
  onChangeIntakeForm?: (kind: GateKind, formDefinitionId: string | null) => void;
  onSetupPayments?: () => void;
}

function PlacedGateRow({
  subjectPlural,
  gate,
  stripePaymentsReady,
  intakeFormOptions,
  onRemove,
  onChangeAppliesTo,
  onChangeIntakeForm,
  onSetupPayments,
}: PlacedGateRowProps) {
  const meta = GATE_KIND_META[gate.kind];
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  // A card gate can't collect anything until the practice can accept payments,
  // so it's configurable but inert until then — say so, and link to setup.
  const showPaymentsWarning = gate.kind === 'card' && !stripePaymentsReady;

  return (
    <Box>
      <Stack
        direction="row"
        spacing={1.5}
        useFlexGap
        sx={{
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          border: 1,
          borderColor: 'grey.100',
          borderRadius: 1.5,
          bgcolor: 'grey.50',
          p: 1,
          pl: 1.5,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {meta.label}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {meta.description}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
            Applies to
          </Typography>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={gate.appliesTo}
            onChange={(_event, value: PatientApplicability | null) => {
              if (value) onChangeAppliesTo?.(gate.kind, value);
            }}
            aria-label={`${meta.label} applies to`}
            sx={{ bgcolor: 'background.paper' }}
          >
            {APPLIES_TO_OPTIONS.map(value => (
              <ToggleButton
                key={value}
                value={value}
                sx={{ textTransform: 'none', px: 1.25, py: 0.5 }}
              >
                {APPLIES_TO_SHORT[value]}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <IconButton
            size="small"
            aria-label={`${meta.label} options`}
            onClick={event => setAnchor(event.currentTarget)}
          >
            <MoreVertOutlinedIcon fontSize="small" />
          </IconButton>
          <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
            <MenuItem
              onClick={() => {
                onRemove?.(gate.kind);
                setAnchor(null);
              }}
            >
              Remove
            </MenuItem>
          </Menu>
        </Stack>
      </Stack>
      {gate.kind === 'paperwork' && (
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mt: 1, pl: 1.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
            Form
          </Typography>
          <TextField
            select
            size="small"
            value={gate.formDefinitionId ?? ''}
            onChange={event => onChangeIntakeForm?.(gate.kind, event.target.value || null)}
            sx={{ minWidth: 260, bgcolor: 'background.paper' }}
            slotProps={{ htmlInput: { 'aria-label': 'Form' } }}
            helperText={
              gate.formDefinitionId
                ? `${subjectPlural[0].toUpperCase() + subjectPlural.slice(1)} complete this form while booking; the booking isn’t made until it’s done.`
                : 'No form selected — bookings are held and your team sends forms afterward.'
            }
          >
            <MenuItem value="">None (send forms later)</MenuItem>
            {intakeFormOptions.map(option => (
              <MenuItem key={option.form_id} value={option.form_id}>
                {option.title}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      )}
      {showPaymentsWarning && (
        <Alert severity="warning" sx={{ mt: 1, borderRadius: 1.5 }}>
          This check stays inactive until you set up payments — bookings won’t require a card yet.{' '}
          <Link
            component="button"
            type="button"
            onClick={() => onSetupPayments?.()}
            sx={{ fontWeight: 600, verticalAlign: 'baseline' }}
          >
            Set up payments
          </Link>
        </Alert>
      )}
    </Box>
  );
}

interface ComingSoonRowProps {
  label: string;
  description: string;
}

/** A planned check, shown disabled with a "Coming soon" badge and no controls. */
function ComingSoonRow({ label, description }: ComingSoonRowProps) {
  return (
    <Stack
      direction="row"
      spacing={1.5}
      useFlexGap
      sx={{
        alignItems: 'center',
        flexWrap: 'wrap',
        border: '1px dashed',
        borderColor: 'grey.300',
        borderRadius: 1.5,
        p: 1,
        pl: 1.5,
      }}
    >
      <Box sx={{ flex: 1, minWidth: 200 }}>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
            {label}
          </Typography>
          <Chip
            label="Coming soon"
            size="small"
            variant="outlined"
            sx={{ height: 20, '& .MuiChip-label': { px: 1, fontSize: 11 } }}
          />
        </Stack>
        <Typography variant="caption" color="text.disabled">
          {description}
        </Typography>
      </Box>
    </Stack>
  );
}

interface AddCheckPickerProps {
  addable: GateKind[];
  onAdd: (kind: GateKind) => void;
}

/**
 * Expands the "Add a requirement" affordance into a single-select group of
 * button-style options (radio semantics via an exclusive ToggleButtonGroup),
 * each carrying the check's name and a description so the choice is informed.
 */
function AddCheckPicker({ addable, onAdd }: AddCheckPickerProps) {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<GateKind | null>(null);
  if (addable.length === 0) return null;

  const reset = () => {
    setOpen(false);
    setSelected(null);
  };
  const commit = () => {
    if (selected) {
      onAdd(selected);
      reset();
    }
  };

  if (!open) {
    return (
      <Button
        size="small"
        startIcon={<AddOutlinedIcon />}
        onClick={() => setOpen(true)}
        sx={{ textTransform: 'none', alignSelf: 'flex-start' }}
      >
        Add a requirement
      </Button>
    );
  }

  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'primary.light',
        borderRadius: 1.5,
        bgcolor: 'action.hover',
        p: 1.5,
      }}
    >
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        Choose a requirement to add
      </Typography>
      <ToggleButtonGroup
        exclusive
        orientation="vertical"
        fullWidth
        value={selected}
        onChange={(_event, value: GateKind | null) => setSelected(value)}
      >
        {addable.map(kind => (
          <ToggleButton
            key={kind}
            value={kind}
            sx={{
              textTransform: 'none',
              justifyContent: 'flex-start',
              alignItems: 'flex-start',
              textAlign: 'left',
              px: 1.5,
              py: 1.25,
              bgcolor: 'background.paper',
            }}
          >
            <Stack spacing={0.25} sx={{ alignItems: 'flex-start' }}>
              <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                {GATE_KIND_META[kind].label}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {GATE_KIND_META[kind].description}
              </Typography>
            </Stack>
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
      <Stack direction="row" spacing={1} sx={{ mt: 1.5, justifyContent: 'flex-end' }}>
        <Button size="small" onClick={reset} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          size="small"
          variant="contained"
          onClick={commit}
          disabled={!selected}
          sx={{ textTransform: 'none' }}
        >
          Add
        </Button>
      </Stack>
    </Box>
  );
}
